(ns app.front.view.group.admin.fees.formula.formula-core
  "Reusable formula editing components for fees domain.
   These components are pure - they receive data and callbacks as props."
  (:require [app.domain.fee.formula :as formula]
            [app.front.clipboard :as clipboard]
            [app.front.ui :as ui]
            [app.front.ui.blip :as ui-blip]
            [app.front.util :as futil]
            [app.front.view.group.admin.fees.formula.formula-events :as ff-events]
            [app.front.view.group.admin.fees.formula.formula-subs :as ff-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [reagent.core :as r]))


(def token-types
  "Visual styling for different token types in formulas"
  {:constant "bg-ui-yellow text-black"
   :operator "bg-brand-pale-blue text-black"
   :paren    "bg-ui-night-sky-100 text-white"
   :comma    "bg-brand-pale-blue text-black"
   :variable "bg-ui-yellow text-black"
   :function "bg-ui-night-sky-100 text-white uppercase"
   :unknown  "bg-ui-red-100 text-white"})


(defn highlight
  "Highlights formula tokens with appropriate colors"
  [formula-tokens fee-variables]
  (when formula-tokens
    (let [identifiers (map :fee-variable/identifier fee-variables)]
      [:div.mt-1.flex.flex-wrap.items-center
       (futil/for-frag [token formula-tokens]
         [:div.mr-1.mb-2.p-1.rounded.font-bold
          {:class (-> (formula/token->type identifiers token) token-types)}
          token])])))


(defn editor
  "Formula editor component.
   Props:
   - entity-id: Unique identifier for this editor instance
   - value: Current formula object {:formula text :tokens [...] :status ... :message ...}
   - disabled?: Whether editor is disabled
   - variables: Available fee variables for tokenization
   - tr: Translation function
   - on-change: Callback (fn [new-formula-text]) when formula changes"
  [{:keys [entity-id value disabled? variables tr on-change]}]
  (let [{:keys [formula tokens invalid? message loading?]} value]
    [:div.col-span-2
     [:div.relative
      [highlight tokens variables]
      (when loading?
        [:div.absolute.z-10 {:style {:bottom "5px" :right "10px"}}
         (cc/inline-loading {:icon-description (tr [:loading])})])
      (when-not disabled?
        [ui/text-area {:id        entity-id
                       :rows      1
                       :value     formula
                       :invalid   invalid?
                       :on-change #(on-change (futil/tval %))}])]
     (when invalid?
       [:div.mt-1.text-xs.text-ui-red-100
        (tr [:fees/invalid-formula])
        (when message
          (str ": " message))])]))


(defn tester
  "Formula tester component for evaluating formulas with test values.
   Props:
   - entity-id: Entity identifier (e.g., fee-template-id or fee-variable-id)
   - field: Field identifier (e.g., :fee-template/quantity-formula)
   - formula: Formula object to test
   - variables: Available fee variables
   - tr: Translation function
   This component manages its own test state internally using formula events/subs."
  [{:keys [entity-id field formula variables tr]}]
  (r/with-let [*expanded? (r/atom false)]
    (let [valid-identifiers (->> (map :fee-variable/identifier variables)
                                 set)
          valid-variables   (->> (:tokens formula)
                                 (filter #(contains? valid-identifiers %))
                                 set)
          test-values       (<sub [::ff-subs/test-values entity-id field])
          test-expression   (<sub [::ff-subs/test-expression entity-id field])
          test-result       (<sub [::ff-subs/test-result entity-id field])]
      (when (seq valid-variables)
        [:div.col-span-2
         [ui/expand-button {:expand-handler  #(swap! *expanded? not)
                            :tr              tr
                            :expanded?       @*expanded?
                            :title-expanded  (tr [:fees/test-formula-expanded])
                            :title-collapsed (tr [:fees/test-formula-collapsed])}]
         (when @*expanded?
           [:<>
            [:div.grid.grid-cols-4.gap-4.mt-4.items-end
             (map
               (fn [variable]
                 ^{:key variable}
                 [:div
                  [:label.p-1.rounded.font-bold
                   {:class (:variable token-types)
                    :for   (str "variable-" variable "-" entity-id "-" field)}
                   variable]
                  [:div.flex.flex-row
                   [ui/text-input {:id        (str "variable-" variable "-" entity-id "-" field)
                                   :value     (get test-values variable)
                                   :on-change #(>evt [::ff-events/change-test-variable
                                                      entity-id field variable (futil/tval %)])}]]])
               valid-variables)]
            [:div.grid.grid-cols-4.gap-4.mt-4.items-center
             (cc/button {:kind     :tertiary
                         :size     :small
                         :on-click #(>evt [::ff-events/evaluate-test entity-id field formula test-values])}
                        (tr [:fees/evaluate]))
             [:div.col-span-3
              [:span (str test-expression " = ")]
              [:span.font-bold test-result]]]])]))))


(defn copy-button
  "Button that copies text to clipboard"
  [classes tr text]
  [ui-blip/blip
   (fn [show-text]
     [:button.bg-transparent.border-0.p-1.mb-1.mr-2.text-sm.rounded.font-bold
      {:class    classes
       :on-click (fn []
                   (show-text (tr [:copied]))
                   (clipboard/copy-text text))}
      text])])


(defn variable-list
  "Displays available variables with copy functionality"
  [tr fee-variables]
  [:div.col-span-2.mt-2.text-sm
   [:h2.mb-2.leading-none.text-sm
    [:span.font-bold (str (tr [:fees/available-variables]) ":")]
    [:span " " (tr [:fees/click-to-copy])]]
   [:div.flex.flex-wrap
    (futil/for-frag [fee-variable fee-variables]
      ^{:key (:fee-variable/identifier fee-variable)}
      [copy-button "bg-ui-yellow text-black" tr (:fee-variable/identifier fee-variable)])]])


(defn help-item [tr [title-key content-key]]
  [:div
   [:div.mb-2.font-bold (tr [title-key])]
   [:div (tr [content-key])]])


(defn create-help-keys [functions]
  (map (fn [key]
         [(keyword "functions-help" (str key "-title"))
          (keyword "functions-help" (str key "-help"))])
       functions))


(defn functions-help
  "Help component showing available functions and operators"
  [tr]
  (r/with-let [*expanded?                      (r/atom false)
               math-functions-help-keys        (create-help-keys formula/supported-math-functions)
               operators-help-keys             (create-help-keys formula/supported-operators)
               conditional-functions-help-keys (create-help-keys formula/supported-conditional-functions)
               if-flag-enabled?                (ff/enabled? :feature-flag.id/fees-if-function)
               alpha-flag-enabled?             (ff/enabled? :feature-flag.id/fee-alpha-fields)]
    [:div.col-span-2
     [:h2.mb-2.leading-none.text-sm
      [:span.font-bold (str (tr [:fees/available-functions]) ":")]
      [:span " " (tr [:fees/click-to-copy])]]
     [:div.flex.flex-wrap
      (map
        (fn [fun]
          ^{:key fun}
          [copy-button "bg-ui-night-sky-100 text-white" tr (str/upper-case fun)])
        (if if-flag-enabled?
          formula/supported-functions+operators
          (->> formula/supported-functions+operators
               (remove #(= "if" %))
               vec)))]
     [ui/expand-button {:button-props    {:class-name "mt-2"}
                        :expand-handler  #(swap! *expanded? not)
                        :tr              tr
                        :expanded?       @*expanded?
                        :title-expanded  (tr [:functions-help/title-expanded])
                        :title-collapsed (tr [:functions-help/title-collapsed])}]
     (when @*expanded?
       [:div
        [:div.py-2.border-b.border-ui-gray.border-solid
         [:h3.text-sm.font-bold (tr [:functions-help/math-functions-header])]
         [:div.mt-4.grid.grid-cols-2.gap-4.text-xs
          (futil/for-frag [help math-functions-help-keys]
            [help-item tr help])]]
        [:div.py-2.border-b.border-ui-gray.border-solid
         [:h3.text-sm.font-bold (tr [:functions-help/math-operators-header])]
         [:div.mt-4.grid.grid-cols-2.gap-4.text-xs
          (futil/for-frag [help operators-help-keys]
            [help-item tr help])]]
        (when if-flag-enabled?
          [:div.py-2.border-b.border-ui-gray.border-solid
           [:h3.text-sm.font-bold (tr [:functions-help/conditional-functions-header])]
           [:div.mt-4.grid.gap-4.text-xs
            (futil/for-frag [help conditional-functions-help-keys]
              [help-item tr help])]
           [:div.mt-4.grid.text-xs
            (futil/for-frag [operator formula/supported-boolean-operators]
              [:div.flex.gap-2.py-1
               [:span.w-6.font-bold operator]
               [:span (tr [(keyword (str "boolean-operators/" operator))])]])
            [:span.pt-2 (tr [:functions-help/nested-ifs])]]])
        (when alpha-flag-enabled?
          [:div.py-2.border-b.border-ui-gray.border-solid
           [:h3.text-sm.font-bold (tr [:fees/support-header])]
           [:div.py-2 [:a {:href (tr [:fees/support-link]) :target "_blank"} (tr [:fees/support-link-text])]]])])]))
